import {
    useRef, useState, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import Service from 'additiv-services';
import ServerError from 'errors/ServerError';
import AdapterError from 'errors/AdapterError';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useFormatting } from 'locale';
import { adaptPortfolioDetails } from '../adapters/adaptPortfolioDetails';

// --- Types ---

interface PortfolioDetailsState {
    data: any; // Ideally replace with a specific interface for adapted portfolio data
    dataRaw: any;
    error: any;
    isLoading: boolean;
}

interface PortfolioDetailsOptions {
    clientId: string | number;
    portfolioId: string | number;
    adaptData?: (data: any, context: any) => any;
    loadInitially?: boolean;
}

// Result of useModelPortfolio hook
interface ModelPortfolioHookResult {
    getModelPortfolio: (id: any, options: { adaptOptions: any }) => Promise<any>;
    [key: string]: any;
}

const initialState: PortfolioDetailsState = {
    data: {
        investmentAllocation: {
            chart: [{
                data: {
                    pie: {}, pieSa: {}, bar: {}, list: {},
                },
            }],
        },
        positions: [],
    },
    dataRaw: {},
    error: null,
    isLoading: false,
};

// --- Hook ---

export const usePortfolioDetails = (
    options: PortfolioDetailsOptions,
    modelPortfolio: Partial<ModelPortfolioHookResult> = {},
    loadInitially = false,
) => {
    const {
        clientId,
        portfolioId,
        adaptData = adaptPortfolioDetails,
    } = options;

    const productId = useRef<string | number | undefined>();
    const [state, setState] = useState<PortfolioDetailsState>(initialState);

    const { getModelPortfolio } = modelPortfolio;

    const { t, i18n: { language } } = useTranslation();
    const { getFormattedDate, getFormattedNumber, getFormattedCurrency } = useFormatting();

    const { data: queryResponse, isLoading: queryIsLoading } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getPortfolioDetails',
        payload: [portfolioId, clientId, { language }],
        enabled: loadInitially,
        staleTime: 10 * 60 * 1000,
        gcTime: 20 * 60 * 1000,
    });

    const processData = async () => {
        const response = queryResponse as any;

        try {
            const { ModelPortfolioId } = response.data;
            let modelData = {};

            if (ModelPortfolioId && typeof getModelPortfolio === 'function') {
                const modelPortfolioOptions = {
                    productID: response?.data?.Product?.Id,
                    portfolioValue: response?.data?.CurrentValue,
                    comparedAllocations: response?.data?.Positions,
                    portfolioSecuritiesValue: response?.data?.InvestedAmount,
                };

                modelData = await getModelPortfolio(ModelPortfolioId, {
                    adaptOptions: modelPortfolioOptions,
                });
            }

            try {
                const adapted = adaptData({ ...modelData, ...response.data }, {
                    clientId,
                    t,
                    getFormattedDate,
                    getFormattedNumber,
                    getFormattedCurrency,
                });

                productId.current = response.data?.Product?.Id;
                setState({
                    data: adapted, dataRaw: response.data, error: null, isLoading: false,
                });
            } catch (err: any) {
                throw new AdapterError(err);
            }
        } catch (err: any) {
            if (Service.isCanceled(err)) {
                setState((prev) => ({ ...prev, isLoading: false }));

                return;
            }

            const wrapped = err?.type !== undefined ? err : new ServerError(err);

            setState((prev) => ({ ...prev, isLoading: false, error: wrapped }));
        }
    };

    useEffect(() => {
        if (!queryResponse) return;

        setState((prev) => ({ ...prev, error: null, isLoading: true }));

        processData();
    }, [queryResponse]);

    return {
        data: state.data,
        dataRaw: state.dataRaw,
        productId: productId.current,
        error: state.error,
        isLoading: queryIsLoading || state.isLoading,
    };
};
